import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { candidatesAPI } from '../api/client';
import { toast } from 'react-toastify';
import '../pages/AnimationsAndStyles.css';

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [candidate, setCandidate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [note, setNote] = useState('');

  /* ================= FETCH ================= */

  const fetchCandidate = async () => {
    try {
      const res = await candidatesAPI.getById(id);
      setCandidate(res.data);
      setFormData(res.data);
    } catch {
      toast.error('Failed to fetch candidate');
    }
  };

  useEffect(() => {
    fetchCandidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ================= CHANGE ================= */

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ================= SAVE ================= */

  const handleSave = async () => {
    try {
      await candidatesAPI.update(id, {
        ...formData,
        note,
      });

      toast.success('Candidate updated successfully');
      setEditMode(false);
      setNote('');
      fetchCandidate();
    } catch {
      toast.error('Update failed');
    }
  };

  if (!candidate) return <div style={styles.card}>Loading...</div>;

  return (
    <>
      {/* ================= INLINE CSS ================= */}
      <style>{css}</style>

      <div className="candidate-card">

        {/* ================= HEADER ================= */}
        <div className="header">
          <div>
            {editMode ? (
              <div className="name-edit">
                <input
                  name="first_name"
                  value={formData.first_name || ''}
                  onChange={handleChange}
                  placeholder="First Name"
                />
                <input
                  name="last_name"
                  value={formData.last_name || ''}
                  onChange={handleChange}
                  placeholder="Last Name"
                />
              </div>
            ) : (
              <h2>{candidate.first_name} {candidate.last_name}</h2>
            )}
          </div>

          <div className="actions">
            <button onClick={() => navigate('/candidates')}>⬅ Back</button>

            {!editMode ? (
              <button className="primary" onClick={() => setEditMode(true)}>
                ✏ Edit
              </button>
            ) : (
              <>
                <button className="success" onClick={handleSave}>💾 Save</button>
                <button
                  className="danger"
                  onClick={() => {
                    setEditMode(false);
                    setFormData(candidate);
                    setNote('');
                  }}
                >
                  ✖ Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* ================= META ================= */}
        <div className="meta">
          <span><strong>Updated By:</strong> {candidate.updated_by_name || '—'}</span>
          <span>
            <strong>Updated At:</strong>{' '}
            {candidate.updated_at
              ? new Date(candidate.updated_at).toLocaleString()
              : '—'}
          </span>
        </div>

        {/* ================= FORM GRID ================= */}
        <div className="grid">
          {renderField('Email', 'email_id')}
          {renderField('Phone', 'phone_number')}
          {renderField('Skills', 'skills', true)}
          {renderField('Education', 'education', true)}
          {renderField('Current Employer', 'custom_current_employer')}
          {renderField('Total Experience (Years)', 'custom_overall_experience_years')}
          {renderField('Relevant Experience (Years)', 'custom_relevant_experience_years')}
          {renderField('Current Salary (LPA)', 'custom_current_salary_lpa')}
          {renderField('Expected Salary (LPA)', 'custom_expected_salary_lpa')}
          {renderField('Notice Period (Days)', 'notice_period')}
          {renderPositionField()}
          {renderStatusField()}
        </div>

        {/* ================= HR NOTE ================= */}
        {editMode && (
          <div className="note-section">
            <label>HR Note</label>
            <textarea
              rows={3}
              placeholder="Reason for update (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        )}
      </div>
    </>
  );

  /* ================= HELPERS ================= */

  function renderField(label, name, textarea = false) {
    return (
      <div className="field">
        <label>{label}</label>
        {editMode ? (
          textarea ? (
            <textarea
              name={name}
              value={formData[name] || ''}
              onChange={handleChange}
              rows={3}
            />
          ) : (
            <input
              name={name}
              value={formData[name] || ''}
              onChange={handleChange}
            />
          )
        ) : (
          <div className="value">{candidate[name] || '—'}</div>
        )}
      </div>
    );
  }

  function renderPositionField() {
    return (
      <div className="field">
        <label>Position</label>
        {editMode ? (
          <select
            name="position"
            value={formData.position || ''}
            onChange={handleChange}
          >
            <option value="">Select</option>
            <option value="Embedded System">Embedded System</option>
            <option value="Embedded Software">Embedded Software</option>
            <option value="Post Silicon Validation">Post Silicon Validation</option>
            <option value="VLSI">VLSI</option>
            <option value="HR">HR</option>
            <option value="Business Unit">Business Unit</option>
            <option value="Others">Others</option>
          </select>
        ) : (
          <div className="value">{candidate.position || '—'}</div>
        )}
      </div>
    );
  }

  function renderStatusField() {
    return (
      <div className="field">
        <label>Status</label>
        {editMode ? (
          <select
            name="status"
            value={formData.status || 'applied'}
            onChange={handleChange}
          >
            <option value="applied">Applied</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="interview">Interview</option>
            <option value="offered">Offered</option>
            <option value="rejected">Rejected</option>
          </select>
        ) : (
          <span className={`status ${candidate.status}`}>
            {candidate.status}
          </span>
        )}
      </div>
    );
  }
}

/* ================= INLINE CSS STRING ================= */

const css = `
.candidate-card {
  background: #fff;
  padding: 24px;
  max-width: 1100px;
  margin: auto;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0,0,0,0.08);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.name-edit input {
  width: 160px;
  margin-right: 8px;
}

.actions button {
  margin-left: 8px;
}

.meta {
  margin: 14px 0 22px;
  font-size: 14px;
  color: #555;
  display: flex;
  gap: 24px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.field label {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.field input,
.field textarea,
.field select {
  padding: 7px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
}

.value {
  padding: 6px 0;
}

.note-section {
  margin-top: 24px;
}

.note-section textarea {
  width: 100%;
  padding: 8px;
}

/* Buttons */
button {
  padding: 6px 12px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
}

.primary { background: #1976d2; color: #fff; }
.success { background: #2e7d32; color: #fff; }
.danger { background: #d32f2f; color: #fff; }

/* Status Badge */
.status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  display: inline-block;
  text-transform: capitalize;
}

.status.applied { background: #e3f2fd; }
.status.shortlisted { background: #fff3cd; }
.status.interview { background: #d1ecf1; }
.status.offered { background: #d4edda; }
.status.rejected { background: #f8d7da; }
`;

/* fallback inline style */
const styles = {
  card: {
    padding: '20px'
  }
};


// import React, { useEffect, useState } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { candidatesAPI } from '../api/client';
// import { toast } from 'react-toastify';

// export default function CandidateDetail() {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   const [candidate, setCandidate] = useState(null);
//   const [editMode, setEditMode] = useState(false);
//   const [formData, setFormData] = useState({});
//   const [note, setNote] = useState('');

//   /* ================= FETCH ================= */

//   useEffect(() => {
//     fetchCandidate();
//   }, [id]);

//   const fetchCandidate = async () => {
//     try {
//       const res = await candidatesAPI.getById(id);
//       setCandidate(res.data);
//       setFormData(res.data);
//     } catch {
//       toast.error('Failed to fetch candidate');
//     }
//   };

//   /* ================= CHANGE ================= */

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   /* ================= SAVE ================= */

//   const handleSave = async () => {
//     try {
//       await candidatesAPI.update(id, {
//         ...formData,
//         note, // 🔥 HR note
//       });

//       toast.success('Candidate updated successfully');
//       setEditMode(false);
//       setNote('');
//       fetchCandidate();
//     } catch {
//       toast.error('Update failed');
//     }
//   };

//   if (!candidate) return <div className="card">Loading...</div>;

//   return (
//     <div className="card">

//       {/* ================= HEADER ================= */}
//       <div className="flex-between">
//         <h2>
//           {editMode ? (
//             <>
//               <input
//                 name="first_name"
//                 value={formData.first_name || ''}
//                 onChange={handleChange}
//                 placeholder="First Name"
//               />
//               <input
//                 name="last_name"
//                 value={formData.last_name || ''}
//                 onChange={handleChange}
//                 placeholder="Last Name"
//                 style={{ marginLeft: 8 }}
//               />
//             </>
//           ) : (
//             <>
//               {candidate.first_name} {candidate.last_name}
//             </>
//           )}
//         </h2>

//         <div>
//           <button onClick={() => navigate('/candidates')}>Back</button>

//           {!editMode ? (
//             <button
//               style={{ marginLeft: '10px' }}
//               onClick={() => setEditMode(true)}
//             >
//               Edit
//             </button>
//           ) : (
//             <>
//               <button
//                 style={{ marginLeft: '10px' }}
//                 onClick={handleSave}
//               >
//                 Save
//               </button>

//               <button
//                 style={{ marginLeft: '10px' }}
//                 className="danger"
//                 onClick={() => {
//                   setEditMode(false);
//                   setFormData(candidate);
//                   setNote('');
//                 }}
//               >
//                 Cancel
//               </button>
//             </>
//           )}
//         </div>
//       </div>

//       <hr />

//       {/* ================= META INFO ================= */}
//       <p>
//         <strong>Last Updated By:</strong>{' '}
//         {candidate.updated_by_name || '—'}
//       </p>

//       <p>
//         <strong>Last Updated At:</strong>{' '}
//         {candidate.updated_at
//           ? new Date(candidate.updated_at).toLocaleString()
//           : '—'}
//       </p>

//       <hr />

//       {/* ================= DETAILS ================= */}
//       {renderField('Email', 'email_id')}
//       {renderField('Phone', 'phone_number')}
//       {renderField('Skills', 'skills', true)}
//       {renderField('Education', 'education', true)}
//       {renderField('Current Employer', 'custom_current_employer')}
//       {renderField('Total Experience (Years)', 'custom_overall_experience_years')}
//       {renderField('Relevant Experience (Years)', 'custom_relevant_experience_years')}
//       {renderField('Current Salary (LPA)', 'custom_current_salary_lpa')}
//       {renderField('Expected Salary (LPA)', 'custom_expected_salary_lpa')}
//       {renderField('Notice Period (Days)', 'notice_period')}
//       {renderPositionField()}
//       {renderStatusField()}

//       {/* ================= HR NOTE ================= */}
//       {editMode && (
//         <>
//           <hr />
//           <label><strong>HR Note (optional)</strong></label>
//           <textarea
//             rows={3}
//             placeholder="Why was this candidate updated?"
//             value={note}
//             onChange={(e) => setNote(e.target.value)}
//           />
//         </>
//       )}
//     </div>
//   );

//   /* ================= FIELD RENDERERS ================= */

//   function renderField(label, name, textarea = false) {
//     return (
//       <p>
//         <strong>{label}:</strong>{' '}
//         {editMode ? (
//           textarea ? (
//             <textarea
//               name={name}
//               value={formData[name] || ''}
//               onChange={handleChange}
//               rows={3}
//             />
//           ) : (
//             <input
//               name={name}
//               value={formData[name] || ''}
//               onChange={handleChange}
//             />
//           )
//         ) : (
//           candidate[name] || '-'
//         )}
//       </p>
//     );
//   }

//   function renderPositionField() {
//     return (
//       <p>
//         <strong>Position:</strong>{' '}
//         {editMode ? (
//           <select
//             name="position"
//             value={formData.position || ''}
//             onChange={handleChange}
//           >
//             <option value="">Select Position</option>
//             <option value="Embedded System">Embedded System</option>
//             <option value="Embedded Software">Embedded Software</option>
//             <option value="Post Silicon Validation">Post Silicon Validation</option>
//             <option value="VLSI">VLSI</option>
//             <option value="Others">Others</option>
//             <option value="HR">HR</option>
//             <option value="Business Unit">Business Unit</option>
//           </select>
//         ) : (
//           candidate.position || '-'
//         )}
//       </p>
//     );
//   }

//   function renderStatusField() {
//     return (
//       <p>
//         <strong>Status:</strong>{' '}
//         {editMode ? (
//           <select
//             name="status"
//             value={formData.status || 'applied'}
//             onChange={handleChange}
//           >
//             <option value="applied">Applied</option>
//             <option value="shortlisted">Shortlisted</option>
//             <option value="interview">Interview</option>
//             <option value="offered">Offered</option>
//             <option value="rejected">Rejected</option>
//           </select>
//         ) : (
//           candidate.status || 'applied'
//         )}
//       </p>
//     );
//   }
// }

// // import React, { useEffect, useState } from 'react';
// // import { useParams, useNavigate } from 'react-router-dom';
// // import { candidatesAPI } from '../api/client';
// // import { toast } from 'react-toastify';

// // export default function CandidateDetail() {
// //   const { id } = useParams();
// //   const navigate = useNavigate();

// //   const [candidate, setCandidate] = useState(null);
// //   const [editMode, setEditMode] = useState(false);
// //   const [formData, setFormData] = useState({});

// //   useEffect(() => {
// //     fetchCandidate();
// //   }, [id]);

// //   const fetchCandidate = async () => {
// //     try {
// //       const res = await candidatesAPI.getById(id);
// //       setCandidate(res.data);
// //       setFormData(res.data);
// //     } catch {
// //       toast.error('Failed to fetch candidate');
// //     }
// //   };

// // const [note, setNote] = useState("");

// //   const handleChange = (e) => {
// //     setFormData({ ...formData, [e.target.name]: e.target.value });
// //   };

// //   const handleSave = async () => {
// //     try {
// //       await candidatesAPI.update(id, formData);
// //       toast.success('Candidate updated successfully');
// //       setEditMode(false);
// //       fetchCandidate();
// //     } catch {
// //       toast.error('Update failed');
// //     }
// //   };

// //   if (!candidate) return <div className="card">Loading...</div>;

// //   return (
// //     <div className="card">
// //       {/* HEADER */}
// //       <div className="flex-between">
// //         <h2>
// //           {editMode ? (
// //             <>
// //               <input
// //                 name="first_name"
// //                 value={formData.first_name || ''}
// //                 onChange={handleChange}
// //                 placeholder="First Name"
// //               />
// //               <input
// //                 name="last_name"
// //                 value={formData.last_name || ''}
// //                 onChange={handleChange}
// //                 placeholder="Last Name"
// //                 style={{ marginLeft: 8 }}
// //               />
// //             </>
// //           ) : (
// //             <>
// //               {candidate.first_name} {candidate.last_name}
// //             </>
// //           )}
// //         </h2>

// //         <div>
// //           <button onClick={() => navigate('/candidates')}>Back</button>

// //           {!editMode ? (
// //             <button
// //               style={{ marginLeft: '10px' }}
// //               onClick={() => setEditMode(true)}
// //             >
// //               Edit
// //             </button>
// //           ) : (
// //             <>
// //               <button
// //                 style={{ marginLeft: '10px' }}
// //                 onClick={handleSave}
// //               >
// //                 Save
// //               </button>
// //               <button
// //                 style={{ marginLeft: '10px' }}
// //                 className="danger"
// //                 onClick={() => {
// //                   setEditMode(false);
// //                   setFormData(candidate);
// //                 }}
// //               >
// //                 Cancel
// //               </button>
// //             </>
// //           )}
// //         </div>
// //       </div>

// //       <hr />

// //       {/* DETAILS */}
// //       {renderField('Email', 'email_id')}
// //       {renderField('Phone', 'phone_number')}
// //       {renderField('Skills', 'skills', true)}
// //       {renderField('Education', 'education', true)}
// //       {renderField('Current Employer', 'custom_current_employer')}
// //       {renderField('Total Experience (Years)', 'custom_overall_experience_years')}
// //       {renderField('Relevant Experience (Years)', 'custom_relevant_experience_years')}
// //       {renderField('Current Salary (LPA)', 'custom_current_salary_lpa')}
// //       {renderField('Expected Salary (LPA)', 'custom_expected_salary_lpa')}
// //       {renderField('Notice Period (Days)', 'notice_period')}
// //       {renderPositionField()}
// //       {renderStatusField()}
// //     </div>
// //   );

// //   /* ================= FIELD RENDERERS ================= */

// //   function renderField(label, name, textarea = false) {
// //     return (
// //       <p>
// //         <strong>{label}:</strong>{' '}
// //         {editMode ? (
// //           textarea ? (
// //             <textarea
// //               name={name}
// //               value={formData[name] || ''}
// //               onChange={handleChange}
// //               rows={3}
// //             />
// //           ) : (
// //             <input
// //               name={name}
// //               value={formData[name] || ''}
// //               onChange={handleChange}
// //             />
// //           )
// //         ) : (
// //           candidate[name] || '-'
// //         )}
// //       </p>
// //     );
// //   }

// //   function renderPositionField() {
// //     return (
// //       <p>
// //         <strong>Position:</strong>{' '}
// //         {editMode ? (
// //           <select
// //             name="position"
// //             value={formData.position || ''}
// //             onChange={handleChange}
// //           >
// //             <option value="">Select Position</option>
// //             <option value="Embedded System">Embedded System</option>
// //             <option value="Embedded Software">Embedded Software</option>
// //             <option value="Post Silicon Validation">Post Silicon Validation</option>
// //             <option value="VLSI">VLSI</option>
// //             <option value="Others">Others</option>
// //             <option value="HR">HR</option>
// //             <option value="Business Unit">Business Unit</option>
// //           </select>
// //         ) : (
// //           candidate.position || '-'
// //         )}
// //       </p>
// //     );
// //   }

// //   function renderStatusField() {
// //     return (
// //       <p>
// //         <strong>Status:</strong>{' '}
// //         {editMode ? (
// //           <select
// //             name="status"
// //             value={formData.status || 'applied'}
// //             onChange={handleChange}
// //           >
// //             <option value="applied">Applied</option>
// //             <option value="shortlisted">Shortlisted</option>
// //             <option value="interview">Interview</option>
// //             <option value="offered">Offered</option>
// //             <option value="rejected">Rejected</option>
// //           </select>
// //         ) : (
// //           candidate.status || 'applied'
// //         )}
// //       </p>
// //     );
// //   }
// // }

// // // import React, { useEffect, useState } from 'react';
// // // import { useParams, useNavigate } from 'react-router-dom';
// // // import { candidatesAPI } from '../api/client';
// // // import { toast } from 'react-toastify';

// // // export default function CandidateDetail() {
// // //   const { id } = useParams();
// // //   const navigate = useNavigate();

// // //   const [candidate, setCandidate] = useState(null);
// // //   const [editMode, setEditMode] = useState(false);
// // //   const [formData, setFormData] = useState({});

// // //   useEffect(() => {
// // //     fetchCandidate();
// // //   }, [id]);

// // //   const fetchCandidate = async () => {
// // //     try {
// // //       const res = await candidatesAPI.getById(id);
// // //       setCandidate(res.data);
// // //       setFormData(res.data); // copy for editing
// // //     } catch {
// // //       toast.error('Failed to fetch candidate');
// // //     }
// // //   };

// // //   const handleChange = (e) => {
// // //     setFormData({ ...formData, [e.target.name]: e.target.value });
// // //   };

// // //   const handleSave = async () => {
// // //     try {
// // //       await candidatesAPI.update(id, formData);
// // //       toast.success('Candidate updated');
// // //       setEditMode(false);
// // //       fetchCandidate();
// // //     } catch {
// // //       toast.error('Update failed');
// // //     }
// // //   };

// // //   if (!candidate) return <div className="card">Loading...</div>;

// // //   return (
// // //     <div className="card">
// // //       {/* HEADER */}
// // //       <div className="flex-between">
// // //         <h2>
// // //           {editMode ? (
// // //             <>
// // //               <input
// // //                 name="first_name"
// // //                 value={formData.firstName || ''}
// // //                 onChange={handleChange}
// // //                 placeholder="First Name"
                
// // //               />
// // //               <input
// // //                 name="last_name"
// // //                 value={formData.last_name || ''}
// // //                 onChange={handleChange}
// // //                 placeholder="Last Name"
// // //               />
// // //             </>
// // //           ) : (
// // //             <>
// // //               {candidate.first_name} {candidate.last_name}
// // //             </>
// // //           )}
// // //         </h2>

// // //         <div>
// // //           <button onClick={() => navigate('/candidates')}>Back</button>

// // //           {!editMode ? (
// // //             <button
// // //               style={{ marginLeft: '10px' }}
// // //               onClick={() => setEditMode(true)}
// // //             >
// // //               Edit
// // //             </button>
// // //           ) : (
// // //             <>
// // //               <button
// // //                 style={{ marginLeft: '10px' }}
// // //                 onClick={handleSave}
// // //               >
// // //                 Save
// // //               </button>
// // //               <button
// // //                 style={{ marginLeft: '10px' }}
// // //                 className="danger"
// // //                 onClick={() => {
// // //                   setEditMode(false);
// // //                   setFormData(candidate); // reset changes
// // //                 }}
// // //               >
// // //                 Cancel
// // //               </button>
// // //             </>
// // //           )}
// // //         </div>
// // //       </div>

// // //       <hr />

// // //       {/* DETAILS */}
// // //       {renderField('Email', 'email_id')}
// // //       {renderField('Phone', 'phone_number')}
// // //       {renderField('Skills', 'skills', true)}
// // //       {renderField('Education', 'education', true)}
// // //       {renderField('Current Employer', 'custom_current_employer')}
// // //       {renderField('Total Experience', 'custom_overall_experience_years')}
// // //       {renderField('Relevant Experience', 'custom_relevant_experience_years')}
// // //       {renderField('Current Salary (LPA)', 'custom_current_salary_lpa')}
// // //       {renderField('Expected Salary (LPA)', 'custom_expected_salary_lpa')}
// // //       {renderField('Notice Period', 'notice_period')}
// // //       {renderField('Position', 'position')}
// // //       {renderField('Status', 'status')}
// // //     </div>
// // //   );

// // //   function renderField(label, name, textarea = false) {
// // //     return (
// // //       <p>
// // //         <strong>{label}:</strong>{' '}
// // //         {editMode ? (
// // //           textarea ? (
// // //             <textarea
// // //               name={name}
// // //               value={formData[name] || ''}
// // //               onChange={handleChange}
// // //             />
// // //           ) : (
// // //             <input
// // //               name={name}
// // //               value={formData[name] || ''}
// // //               onChange={handleChange}
// // //             />
// // //           )
// // //         ) : (
// // //           candidate[name] || '-'
// // //         )}
// // //       </p>
// // //     );
// // //   }
// // // }

// // // // import React, { useEffect, useState } from 'react';
// // // // import { useParams, useNavigate } from 'react-router-dom';
// // // // import { candidatesAPI } from '../api/client';
// // // // import { toast } from 'react-toastify';

// // // // export default function CandidateDetail() {
// // // //   const { id } = useParams();
// // // //   const navigate = useNavigate();
// // // //   const [candidate, setCandidate] = useState(null);

// // // //   useEffect(() => {
// // // //     fetchCandidate();
// // // //   }, [id]);

// // // //   const fetchCandidate = async () => {
// // // //     try {
// // // //       const res = await candidatesAPI.getById(id);
// // // //       setCandidate(res.data);
// // // //     } catch {
// // // //       toast.error('Failed to load candidate');
// // // //     }
// // // //   };

// // // //   if (!candidate) return <div className="card">Loading...</div>;

// // // //   return (
// // // //     <div className="card">
// // // //       <h2>{candidate.first_name} {candidate.last_name}</h2>

// // // //       <p><strong>Email:</strong> {candidate.email_id || '-'}</p>
// // // //       <p><strong>Phone:</strong> {candidate.phone_number || '-'}</p>
// // // //       <p><strong>Skills:</strong> {candidate.skills || '-'}</p>
// // // //       <p><strong>Education:</strong> {candidate.education || '-'}</p>
// // // //       <p><strong>Current Employer:</strong> {candidate.custom_current_employer || '-'}</p>
// // // //       <p><strong>Total Experience:</strong> {candidate.custom_overall_experience_years || '-'}</p>
// // // //       <p><strong>Relevant Experience:</strong> {candidate.custom_relevant_experience_years || '-'}</p>
// // // //       <p><strong>Current Salary:</strong> {candidate.custom_current_salary_lpa || '-'}</p>
// // // //       <p><strong>Expected Salary:</strong> {candidate.custom_expected_salary_lpa || '-'}</p>
// // // //       <p><strong>Notice Period:</strong> {candidate.notice_period || '-'}</p>
// // // //       <p><strong>Position:</strong> {candidate.position || '-'}</p>
// // // //       <p><strong>Status:</strong> {candidate.status || 'applied'}</p>

// // // //       <button onClick={() => navigate('/candidates')}>Back</button>
// // // //     </div>
// // // //   );
// // // // }

// // // // // import React, { useEffect, useState } from 'react';
// // // // // import { useParams, useNavigate } from 'react-router-dom';
// // // // // import { candidatesAPI } from '../api/client';
// // // // // import { toast } from 'react-toastify';

// // // // // export default function CandidateDetail() {
// // // // //   const { id } = useParams();
// // // // //   const navigate = useNavigate();

// // // // //   const [candidate, setCandidate] = useState(null);
// // // // //   const [loading, setLoading] = useState(true);

// // // // //   /* ================= FETCH CANDIDATE ================= */

// // // // //   useEffect(() => {
// // // // //     fetchCandidate();
// // // // //     // eslint-disable-next-line
// // // // //   }, [id]);

// // // // //   const fetchCandidate = async () => {
// // // // //     try {
// // // // //       const res = await candidatesAPI.getById(id);
// // // // //       setCandidate(res.data);
// // // // //     } catch (error) {
// // // // //       console.error(error);
// // // // //       toast.error('Failed to fetch candidate');
// // // // //     } finally {
// // // // //       setLoading(false);
// // // // //     }
// // // // //   };

// // // // //   /* ================= DELETE ================= */

// // // // //   const handleDelete = async () => {
// // // // //     if (!window.confirm('Are you sure you want to delete this candidate?')) return;

// // // // //     try {
// // // // //       await candidatesAPI.delete(id);
// // // // //       toast.success('Candidate deleted successfully');
// // // // //       navigate('/candidates');
// // // // //     } catch {
// // // // //       toast.error('Failed to delete candidate');
// // // // //     }
// // // // //   };

// // // // //   /* ================= UI STATES ================= */

// // // // //   if (loading) {
// // // // //     return <div className="card">Loading candidate details...</div>;
// // // // //   }

// // // // //   if (!candidate) {
// // // // //     return <div className="card">Candidate not found</div>;
// // // // //   }

// // // // //   /* ================= UI ================= */

// // // // //   return (
// // // // //     <div className="card">
// // // // //       <div className="flex-between">
// // // // //         <h2>
// // // // //           {candidate.firstname} {candidate.lastname}
// // // // //         </h2>

// // // // //         <div>
// // // // //           <button onClick={() => navigate('/candidates')}>
// // // // //             Back
// // // // //           </button>
// // // // //           <button
// // // // //             className="danger"
// // // // //             style={{ marginLeft: '10px' }}
// // // // //             onClick={handleDelete}
// // // // //           >
// // // // //             Delete
// // // // //           </button>
// // // // //         </div>
// // // // //       </div>

// // // // //       <hr />

// // // // //       <p>
// // // // //         <strong>Email:</strong>{' '}
// // // // //         {candidate.email_id|| '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Phone:</strong>{' '}
// // // // //         {candidate.phone_number || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Skills:</strong>{' '}
// // // // //         {candidate.skills || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Education:</strong>{' '}
// // // // //         {candidate.education || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Current Employer:</strong>{' '}
// // // // //         {candidate.custom_current_employer || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Total Experience:</strong>{' '}
// // // // //         {candidate.custom_overall_experience_years
// // // // //           ? `${candidate.custom_overall_experience_years} years`
// // // // //           : '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Relevant Experience:</strong>{' '}
// // // // //         {candidate.custom_relevant_experience_years
// // // // //           ? `${candidate.custom_relevant_experience_years} years`
// // // // //           : '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Current Salary (LPA):</strong>{' '}
// // // // //         {candidate.custom_current_salary_lpa || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Expected Salary (LPA):</strong>{' '}
// // // // //         {candidate.custom_expected_salary_lpa || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Notice Period:</strong>{' '}
// // // // //         {candidate.notice_period || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Position:</strong>{' '}
// // // // //         {candidate.position || '-'}
// // // // //       </p>

// // // // //       <p>
// // // // //         <strong>Status:</strong>{' '}
// // // // //         <span style={{ textTransform: 'capitalize' }}>
// // // // //           {candidate.status}
// // // // //         </span>
// // // // //       </p>
// // // // //     </div>
// // // // //   );
// // // // // }


// // // // // // import React, { useEffect, useState } from 'react';
// // // // // // import { useParams } from 'react-router-dom';
// // // // // // import { candidatesAPI, resumesAPI, interviewsAPI, offersAPI, emailsAPI, aiAPI } from '../api/client';
// // // // // // import { toast } from 'react-toastify';

// // // // // // export default function CandidateDetail() {
// // // // // //   const { id } = useParams();
// // // // // //   const [candidate, setCandidate] = useState(null);
// // // // // //   const [resumes, setResumes] = useState([]);
// // // // // //   const [interviews, setInterviews] = useState([]);
// // // // // //   const [offers, setOffers] = useState([]);
// // // // // //   const [loading, setLoading] = useState(true);
// // // // // //   const [showInterviewForm, setShowInterviewForm] = useState(false);
// // // // // //   const [showOfferForm, setShowOfferForm] = useState(false);
// // // // // //   const [interviewData, setInterviewData] = useState({
// // // // // //     scheduled_date: '',
// // // // // //     interview_type: '',
// // // // // //     interviewer_name: '',
// // // // // //     notes: ''
// // // // // //   });
// // // // // //   const [offerData, setOfferData] = useState({
// // // // // //     position: '',
// // // // // //     salary: '',
// // // // // //     start_date: '',
// // // // // //     department: '',
// // // // // //     terms_conditions: ''
// // // // // //   });

// // // // // //   useEffect(() => {
// // // // // //     fetchData();
// // // // // //   }, [id]);

// // // // // //   const fetchData = async () => {
// // // // // //     try {
// // // // // //       const [candRes, resRes, intRes, ofRes] = await Promise.all([
// // // // // //         candidatesAPI.getById(id),
// // // // // //         resumesAPI.getByCandidate(id),
// // // // // //         interviewsAPI.getByCandidate(id),
// // // // // //         offersAPI.getByCandidate(id)
// // // // // //       ]);

// // // // // //       setCandidate(candRes.data);
// // // // // //       setResumes(resRes.data);
// // // // // //       setInterviews(intRes.data);
// // // // // //       setOffers(ofRes.data);
// // // // // //     } catch (error) {
// // // // // //       toast.error('Failed to fetch candidate details');
// // // // // //     } finally {
// // // // // //       setLoading(false);
// // // // // //     }
// // // // // //   };

// // // // // //   const handleResumeUpload = async (e) => {
// // // // // //     const file = e.target.files[0];
// // // // // //     if (!file) return;

// // // // // //     try {
// // // // // //       const formData = new FormData();
// // // // // //       formData.append('resume', file);
// // // // // //       formData.append('candidateId', id);

// // // // // //       const response = await resumesAPI.upload(formData);
// // // // // //       toast.success('Resume uploaded successfully');
// // // // // //       fetchData();
// // // // // //     } catch (error) {
// // // // // //       toast.error('Failed to upload resume');
// // // // // //     }
// // // // // //   };

// // // // // //   const handleInterviewSubmit = async (e) => {
// // // // // //     e.preventDefault();
// // // // // //     try {
// // // // // //       await interviewsAPI.create({
// // // // // //         candidate_id: id,
// // // // // //         ...interviewData
// // // // // //       });
// // // // // //       toast.success('Interview scheduled');
// // // // // //       setInterviewData({ scheduled_date: '', interview_type: '', interviewer_name: '', notes: '' });
// // // // // //       setShowInterviewForm(false);
// // // // // //       fetchData();
// // // // // //     } catch (error) {
// // // // // //       toast.error('Failed to schedule interview');
// // // // // //     }
// // // // // //   };

// // // // // //   const handleOfferSubmit = async (e) => {
// // // // // //     e.preventDefault();
// // // // // //     try {
// // // // // //       await offersAPI.create({
// // // // // //         candidate_id: id,
// // // // // //         ...offerData
// // // // // //       });
// // // // // //       toast.success('Offer created');
// // // // // //       setOfferData({ position: '', salary: '', start_date: '', department: '', terms_conditions: '' });
// // // // // //       setShowOfferForm(false);
// // // // // //       fetchData();
// // // // // //     } catch (error) {
// // // // // //       toast.error('Failed to create offer');
// // // // // //     }
// // // // // //   };

// // // // // //   const handleSendInterviewEmail = async (interviewId) => {
// // // // // //     try {
// // // // // //       await emailsAPI.sendInterview({
// // // // // //         candidate_id: id,
// // // // // //         interview_id: interviewId,
// // // // // //         email_service: 'gmail'
// // // // // //       });
// // // // // //       toast.success('Interview email sent');
// // // // // //     } catch (error) {
// // // // // //       toast.error('Failed to send email');
// // // // // //     }
// // // // // //   };

// // // // // //   if (loading) return <div className="card"><p className="loading">Loading...</p></div>;
// // // // // //   if (!candidate) return <div className="card"><p className="error">Candidate not found</p></div>;

// // // // // //   return (
// // // // // //     <div className="card">
// // // // // //       <h2>{candidate.name}</h2>
// // // // // //       <div style={{ marginBottom: '2rem' }}>
// // // // // //         <p><strong>Email:</strong> {candidate.email}</p>
// // // // // //         <p><strong>Phone:</strong> {candidate.phone}</p>
// // // // // //         <p><strong>Position:</strong> {candidate.position}</p>
// // // // // //         <p><strong>Status:</strong> <span className={`badge badge-${candidate.status}`}>{candidate.status}</span></p>
// // // // // //       </div>

// // // // // //       {/* Resume Section */}
// // // // // //       <div style={{ marginBottom: '2rem' }}>
// // // // // //         <h3>Resume</h3>
// // // // // //         <input type="file" accept=".pdf,.docx" onChange={handleResumeUpload} />
// // // // // //         {resumes.length > 0 && (
// // // // // //           <div>
// // // // // //             <h4>Uploaded Resumes:</h4>
// // // // // //             {resumes.map(resume => (
// // // // // //               <div key={resume.id} style={{ padding: '0.5rem', backgroundColor: '#f0f0f0', marginBottom: '0.5rem', borderRadius: '4px' }}>
// // // // // //                 <p><strong>{resume.file_name}</strong></p>
// // // // // //                 <p style={{ fontSize: '0.9rem', color: '#666' }}>Uploaded: {new Date(resume.uploaded_at).toLocaleDateString()}</p>
// // // // // //               </div>
// // // // // //             ))}
// // // // // //           </div>
// // // // // //         )}
// // // // // //       </div>

// // // // // //       {/* Interviews Section */}
// // // // // //       <div style={{ marginBottom: '2rem' }}>
// // // // // //         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
// // // // // //           <h3>Interviews</h3>
// // // // // //           <button onClick={() => setShowInterviewForm(!showInterviewForm)}>
// // // // // //             {showInterviewForm ? 'Cancel' : 'Schedule Interview'}
// // // // // //           </button>
// // // // // //         </div>

// // // // // //         {showInterviewForm && (
// // // // // //           <form onSubmit={handleInterviewSubmit} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
// // // // // //             <div className="form-group">
// // // // // //               <label>Interview Date</label>
// // // // // //               <input type="datetime-local" value={interviewData.scheduled_date} onChange={(e) => setInterviewData({...interviewData, scheduled_date: e.target.value})} required />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Interview Type</label>
// // // // // //               <input type="text" value={interviewData.interview_type} onChange={(e) => setInterviewData({...interviewData, interview_type: e.target.value})} />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Interviewer Name</label>
// // // // // //               <input type="text" value={interviewData.interviewer_name} onChange={(e) => setInterviewData({...interviewData, interviewer_name: e.target.value})} />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Notes</label>
// // // // // //               <textarea value={interviewData.notes} onChange={(e) => setInterviewData({...interviewData, notes: e.target.value})} />
// // // // // //             </div>
// // // // // //             <button type="submit">Schedule Interview</button>
// // // // // //           </form>
// // // // // //         )}

// // // // // //         {interviews.length > 0 ? (
// // // // // //           <table>
// // // // // //             <thead>
// // // // // //               <tr>
// // // // // //                 <th>Date</th>
// // // // // //                 <th>Type</th>
// // // // // //                 <th>Interviewer</th>
// // // // // //                 <th>Status</th>
// // // // // //                 <th>Actions</th>
// // // // // //               </tr>
// // // // // //             </thead>
// // // // // //             <tbody>
// // // // // //               {interviews.map(interview => (
// // // // // //                 <tr key={interview.id}>
// // // // // //                   <td>{new Date(interview.scheduled_date).toLocaleString()}</td>
// // // // // //                   <td>{interview.interview_type}</td>
// // // // // //                   <td>{interview.interviewer_name}</td>
// // // // // //                   <td><span className={`badge badge-${interview.status}`}>{interview.status}</span></td>
// // // // // //                   <td>
// // // // // //                     <button onClick={() => handleSendInterviewEmail(interview.id)} className="btn btn-secondary">Send Email</button>
// // // // // //                   </td>
// // // // // //                 </tr>
// // // // // //               ))}
// // // // // //             </tbody>
// // // // // //           </table>
// // // // // //         ) : (
// // // // // //           <p style={{ color: '#666' }}>No interviews scheduled</p>
// // // // // //         )}
// // // // // //       </div>

// // // // // //       {/* Offers Section */}
// // // // // //       <div>
// // // // // //         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
// // // // // //           <h3>Offers</h3>
// // // // // //           <button onClick={() => setShowOfferForm(!showOfferForm)}>
// // // // // //             {showOfferForm ? 'Cancel' : 'Create Offer'}
// // // // // //           </button>
// // // // // //         </div>

// // // // // //         {showOfferForm && (
// // // // // //           <form onSubmit={handleOfferSubmit} style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
// // // // // //             <div className="form-group">
// // // // // //               <label>Position</label>
// // // // // //               <input type="text" value={offerData.position} onChange={(e) => setOfferData({...offerData, position: e.target.value})} required />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Salary</label>
// // // // // //               <input type="text" value={offerData.salary} onChange={(e) => setOfferData({...offerData, salary: e.target.value})} />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Start Date</label>
// // // // // //               <input type="date" value={offerData.start_date} onChange={(e) => setOfferData({...offerData, start_date: e.target.value})} />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Department</label>
// // // // // //               <input type="text" value={offerData.department} onChange={(e) => setOfferData({...offerData, department: e.target.value})} />
// // // // // //             </div>
// // // // // //             <div className="form-group">
// // // // // //               <label>Terms & Conditions</label>
// // // // // //               <textarea value={offerData.terms_conditions} onChange={(e) => setOfferData({...offerData, terms_conditions: e.target.value})} />
// // // // // //             </div>
// // // // // //             <button type="submit">Create Offer</button>
// // // // // //           </form>
// // // // // //         )}

// // // // // //         {offers.length > 0 ? (
// // // // // //           <table>
// // // // // //             <thead>
// // // // // //               <tr>
// // // // // //                 <th>Position</th>
// // // // // //                 <th>Salary</th>
// // // // // //                 <th>Start Date</th>
// // // // // //                 <th>Status</th>
// // // // // //               </tr>
// // // // // //             </thead>
// // // // // //             <tbody>
// // // // // //               {offers.map(offer => (
// // // // // //                 <tr key={offer.id}>
// // // // // //                   <td>{offer.position}</td>
// // // // // //                   <td>{offer.salary}</td>
// // // // // //                   <td>{new Date(offer.start_date).toLocaleDateString()}</td>
// // // // // //                   <td><span className={`badge badge-${offer.status}`}>{offer.status}</span></td>
// // // // // //                 </tr>
// // // // // //               ))}
// // // // // //             </tbody>
// // // // // //           </table>
// // // // // //         ) : (
// // // // // //           <p style={{ color: '#666' }}>No offers created</p>
// // // // // //         )}
// // // // // //       </div>
// // // // // //     </div>
// // // // // //   );
// // // // // // }
